import React, { useEffect } from 'react'
import { Editor, EditorContent, useEditor } from '@tiptap/react'
import { JSONContent } from '@tiptap/core'
import Placeholder from '@tiptap/extension-placeholder'
import clsx from 'clsx'
import { createStyles, makeStyles } from '@material-ui/core/styles'

import { richTextExtensions } from '../../utils/richtext/extensions'

const useStyles = makeStyles(() =>
  createStyles({
    toolbar: {
      display: 'flex',
      gap: 4,
      padding: '4px 0',
    },
    button: {
      minWidth: 28,
      height: 24,
      border: '1px solid #d5d5d5',
      borderRadius: 3,
      background: '#fff',
      cursor: 'pointer',
      fontSize: 12,
      lineHeight: 1,
      padding: '0 6px',
    },
    active: {
      background: '#e5e5e5',
    },
    // Mirrors the draft-js placeholder styling the editors relied on.
    editor: {
      '& .placeholder': { color: 'red', fontWeight: 'bold' },
      // The old editor drew the hidden link as blue plain text (a <span>). It is
      // an <a> now, so undo the browser's underline and hand cursor to match.
      '& .account-page-url': { color: 'blue', textDecoration: 'none', cursor: 'text' },
      '& .ProseMirror': { outline: 'none' },
      // src/index.css resets `p { margin: 0 }`. draft-js was unaffected because it
      // rendered blocks as <div> and react-draft-wysiwyg.css gave them
      // `.public-DraftStyleDefault-block { margin: 1em 0 }`. TipTap renders <p>,
      // so that spacing has to be restored here or paragraphs run together.
      '& .ProseMirror p': { margin: '1em 0' },
      // draft-js: `.public-DraftStyleDefault-ol/ul { margin: 16px 0; padding: 0 }`
      // with a 1.5em indent on the first level.
      '& .ProseMirror ul, & .ProseMirror ol': { margin: '16px 0', paddingLeft: '1.5em' },
      // List items were blocks too, so they carried the same 1em. The paragraph
      // TipTap nests inside each <li> must not double it.
      '& .ProseMirror li': { margin: '1em 0' },
      '& .ProseMirror li p': { margin: 0 },
      '& .ProseMirror p.is-editor-empty:first-child::before': {
        color: '#9e9e9e',
        content: 'attr(data-placeholder)',
        float: 'left',
        height: 0,
        pointerEvents: 'none',
      },
    },
  })
)

export type ToolbarOption = 'bold' | 'italic' | 'bulletList' | 'orderedList'

export interface RichTextEditorProps {
  /** Editor content as HTML. */
  value: string
  /** `doc` is the TipTap document, for callers that need structure rather than HTML. */
  onChange: (html: string, doc: JSONContent) => void
  onBlur?: () => void
  onFocus?: () => void
  placeholder?: string
  readOnly?: boolean
  editorClassName?: string
  toolbarClassName?: string
  toolbarHidden?: boolean
  toolbarOptions?: ToolbarOption[]
  spellCheck?: boolean
  /** Gives the parent access to the editor for imperative commands (snippet insertion). */
  editorRef?: React.MutableRefObject<Editor | null>
}

const LABELS: Record<ToolbarOption, string> = {
  bold: 'B',
  italic: 'I',
  bulletList: '• List',
  orderedList: '1. List',
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  readOnly = false,
  editorClassName,
  toolbarClassName,
  toolbarHidden = false,
  toolbarOptions = ['bold', 'italic', 'bulletList', 'orderedList'],
  spellCheck = true,
  editorRef,
}) => {
  const classes = useStyles()

  const editor = useEditor({
    extensions: [...richTextExtensions(), Placeholder.configure({ placeholder: placeholder || '' })],
    content: value || '',
    editable: !readOnly,
    editorProps: {
      attributes: { spellcheck: String(spellCheck) },
    },
    // TipTap emits `update` for more than content edits (setEditable does, for
    // one). Only a real document change is an onChange, otherwise consumers
    // like react-final-form get written to on mount and mid-submit (CCB-5711).
    onUpdate: ({ editor: instance, transaction }) => {
      if (!transaction.docChanged) return
      onChange(instance.getHTML(), instance.getJSON())
    },
    onBlur: () => onBlur && onBlur(),
    onFocus: () => onFocus && onFocus(),
  })

  // Register the editor for consumers that need imperative access (snippet
  // insertion, the composition window's focus check). The ref is shared module
  // wide, so it must be cleared on unmount: leaving a destroyed editor behind
  // would pass their `if (!editor) return` guards and then throw on use.
  useEffect(() => {
    if (!editorRef) return
    editorRef.current = editor
    return () => {
      if (editorRef.current === editor) {
        editorRef.current = null
      }
    }
  }, [editor, editorRef])

  // Adopt content changed from outside (templates, CTA sync, draft restore)
  // without clobbering what the writer is currently typing.
  useEffect(() => {
    if (!editor) return
    if (editor.isFocused) return
    if (value === editor.getHTML()) return
    editor.commands.setContent(value || '', { emitUpdate: false })
  }, [value, editor])

  useEffect(() => {
    // Second argument suppresses the `update` event setEditable would otherwise
    // emit: toggling readOnly is not a content change.
    if (editor) editor.setEditable(!readOnly, false)
  }, [readOnly, editor])

  const toggle = (option: ToolbarOption) => {
    if (!editor) return
    const chain = editor.chain().focus()
    switch (option) {
      case 'bold':
        chain.toggleBold().run()
        break
      case 'italic':
        chain.toggleItalic().run()
        break
      case 'bulletList':
        chain.toggleBulletList().run()
        break
      case 'orderedList':
        chain.toggleOrderedList().run()
        break
    }
  }

  return (
    <>
      {!toolbarHidden && (
        <div className={clsx(classes.toolbar, toolbarClassName)}>
          {toolbarOptions.map(option => (
            <button
              key={option}
              type="button"
              aria-label={option}
              // Keep focus in the document so the command applies to the selection.
              onMouseDown={e => e.preventDefault()}
              onClick={() => toggle(option)}
              className={clsx(classes.button, editor && editor.isActive(option) ? classes.active : null)}
            >
              {LABELS[option]}
            </button>
          ))}
        </div>
      )}
      <EditorContent className={clsx(classes.editor, editorClassName)} editor={editor} />
    </>
  )
}

export default RichTextEditor
